import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Employee } from '../types';

interface EmployeesState {
  employees: Employee[];
  filteredEmployees: Employee[];
  selectedEmployee: Employee | null;
  filters: {
    department: string | null;
    location: string | null;
    tenure: string | null;
    roleLevel: string | null;
  };
  loading: boolean;
  error: string | null;
}

const initialState: EmployeesState = {
  employees: [],
  filteredEmployees: [],
  selectedEmployee: null,
  filters: {
    department: null,
    location: null,
    tenure: null,
    roleLevel: null,
  },
  loading: false,
  error: null,
};

const employeesSlice = createSlice({
  name: 'employees',
  initialState,
  reducers: {
    setEmployees: (state, action: PayloadAction<Employee[]>) => {
      state.employees = action.payload;
      state.filteredEmployees = action.payload;
    },
    setSelectedEmployee: (state, action: PayloadAction<Employee | null>) => {
      state.selectedEmployee = action.payload;
    },
    applyFilters: (state, action: PayloadAction<Partial<EmployeesState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
      
      let filtered = [...state.employees];
      
      if (state.filters.department) {
        filtered = filtered.filter(e => e.department === state.filters.department);
      }
      if (state.filters.location) {
        filtered = filtered.filter(e => e.location === state.filters.location);
      }
      if (state.filters.roleLevel) {
        filtered = filtered.filter(e => e.roleLevel === state.filters.roleLevel);
      }
      if (state.filters.tenure) {
        const [min, max] = state.filters.tenure.split('-').map(Number);
        filtered = filtered.filter(e => e.tenure >= min && (!max || e.tenure <= max));
      }
      
      state.filteredEmployees = filtered;
    },
    clearFilters: (state) => {
      state.filters = {
        department: null,
        location: null,
        tenure: null,
        roleLevel: null,
      };
      state.filteredEmployees = state.employees;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setEmployees,
  setSelectedEmployee,
  applyFilters,
  clearFilters,
  setLoading,
  setError,
} = employeesSlice.actions;

export default employeesSlice.reducer;